import { useEffect } from "react";
import { useSubscription } from "@apollo/client";
import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { SCREEN_STATUS_CHANGED } from "../api/services/screenService";
import { onWsReconnected } from "../api/apolloClient";
import type { Screen, ScreenResult } from "../schemas/screenSchema";

interface ScreenStatusChangedEvent {
  screenId: string;
  screenName: string;
  isOn: boolean;
  changedAt: string;
}

/**
 * Applies a status change to a single screen inside a ScreenResult page.
 *
 * Returns the SAME array reference when the screen isn't present, so React
 * Query's structural sharing skips re-rendering cached variants that don't
 * contain the changed screen.
 */
const patchScreens = (
  screens: Screen[],
  event: ScreenStatusChangedEvent,
): Screen[] => {
  let changed = false;
  const next = screens.map((screen) => {
    if (screen.id !== event.screenId) return screen;
    changed = true;
    return {
      ...screen,
      status: event.isOn,
      // Backend semantics: lastOnAt is null while ON, and the moment it went
      // OFF otherwise (see Screen model hooks). Mirror that here.
      lastOnAt: event.isOn ? undefined : event.changedAt,
    };
  });
  return changed ? next : screens;
};

/**
 * Listens for real-time screen status changes (on/off) via GraphQL subscription
 * and patches the affected screen directly in the React Query cache.
 *
 * These events fire frequently and in bursts (device heartbeats, manual
 * toggles, and the backend's periodic stale-sweep). Previously each event ran a
 * blanket `invalidateQueries(["screens"])`, forcing a full network refetch of
 * the (multi-page, network-only) infinite list. Overlapping refetches made
 * already-loaded rows briefly disappear and reappear. Surgically updating the
 * one changed screen keeps the status live with zero refetch and zero flicker.
 */
export const useScreenStatusSubscription = () => {
  const queryClient = useQueryClient();

  // Status events published while the WebSocket was down are gone for good —
  // the cache keeps whatever badge was last patched in. Refetch on reconnect
  // so the list re-syncs with the database instead of staying stale until a
  // manual hard refresh.
  useEffect(
    () =>
      onWsReconnected(() => {
        queryClient.invalidateQueries({ queryKey: ["screens"] });
      }),
    [queryClient],
  );

  useSubscription<{
    screenStatusChanged: ScreenStatusChangedEvent;
  }>(SCREEN_STATUS_CHANGED, {
    onData: ({ data }) => {
      const event = data?.data?.screenStatusChanged;
      if (!event) return;

      // Infinite list (Screens page): data is paged.
      queryClient.setQueriesData<InfiniteData<ScreenResult>>(
        { queryKey: ["screens", "infinite"] },
        (old) => {
          if (!old) return old;
          let touched = false;
          const pages = old.pages.map((page) => {
            const screens = patchScreens(page.screens, event);
            if (screens === page.screens) return page;
            touched = true;
            return { ...page, screens };
          });
          return touched ? { ...old, pages } : old;
        },
      );

      // Plain (non-infinite) screen lists used elsewhere (e.g. dropdowns).
      queryClient.setQueriesData<ScreenResult>(
        { queryKey: ["screens"], exact: false },
        (old) => {
          // Skip the infinite variants handled above (they have no `screens`
          // array at the top level).
          if (!old || !Array.isArray(old.screens)) return old;
          const screens = patchScreens(old.screens, event);
          return screens === old.screens ? old : { ...old, screens };
        },
      );
    },
    onError: (error) => {
      console.error("[ScreenStatusSubscription] Error:", error.message);
    },
  });
};
